import "./RequestScreenButton.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { Case, User } from "pwapi";

import { requestScreen } from "shared/actions/caseDetailActions";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { sortSpecimens, specimen_source } from "shared/utils/specimenUtils";

import { useAppSelector } from "store/hooks";

import { DialogEnum } from "./dialogs/Dialog";
import { isFinalized, isScreened } from "./dialogs/utils";

const SpecimenSelector = ({
  caseDetailObject,
  slideId,
  onSelect,
  onClose,
}: {
  caseDetailObject: CaseDetailState;
  slideId?: number;
  onSelect: Function;
  onClose: Function;
}) => {
  const [selectedSpecimens, setSelectedSpecimens] = useState<number[]>(
    caseDetailObject.specimens?.map((spec) => spec.id || -1) || []
  );

  const allViewed =
    selectedSpecimens.length &&
    !!caseDetailObject.slides.length &&
    caseDetailObject.slides
      .filter((s) => s.specimen_id && selectedSpecimens.includes(s.specimen_id))
      .every(
        // Slide was previously viewed OR is the one they're looking at right now
        (s) => s.specimen_id && (s.was_previously_viewed || s.id === slideId)
      );

  const uncheckSpecimen = (id: number) =>
    setSelectedSpecimens(selectedSpecimens.filter((specId) => specId !== id));
  const checkSpecimen = (id: number) =>
    setSelectedSpecimens([...selectedSpecimens, id]);

  const handleOnChange = (id: number) => {
    selectedSpecimens.includes(id) ? uncheckSpecimen(id) : checkSpecimen(id);
  };

  return (
    <div className="SpecimenSelector">
      {sortSpecimens(caseDetailObject.specimens).map((spec) => {
        return (
          <label key={spec.id}>
            <input
              type="checkbox"
              checked={selectedSpecimens.includes(spec.id || 0)}
              value={spec.id}
              onChange={(e) => handleOnChange(parseInt(e.target.value))}
            />
            <strong>{spec.name} - </strong>
            <span>{specimen_source(spec, caseDetailObject)}</span>
          </label>
        );
      })}
      {!allViewed && (
        <span className="selection--error">
          {!selectedSpecimens.length
            ? "At least one specimen must be selected to continue"
            : !allViewed && "All slides must be viewed on selected specimens"}
        </span>
      )}
      <div className="controls">
        <button className="Button Button--red" onClick={() => onClose()}>
          Cancel
        </button>
        <button
          className="Button Button--teal"
          disabled={!allViewed}
          onClick={() => onSelect(selectedSpecimens)}
        >
          Submit
        </button>
      </div>
    </div>
  );
};

const RequestScreenButton = ({
  caseDetailObject,
  slideId,
  currentUser,
  dialog,
  setDialog,
}: {
  caseDetailObject: CaseDetailState;
  slideId?: number;
  currentUser?: User;
  dialog?: DialogEnum | null;
  setDialog?: Function;
}) => {
  const dispatch = useDispatch();

  const screenPerSpecimen = useAppSelector((state) =>
    selectFeatureFlag(state, "screenPerSpecimen")
  );
  const [screenerRequested, setScreenerRequested] = useState<boolean>(false);
  const [showSpecimens, setShowSpecimens] = useState<boolean>(false);
  useEffect(() => {
    setScreenerRequested(false); // Get the button unstuck after Next Case
  }, [caseDetailObject.id]);

  const waitingForScreening =
    screenerRequested || caseDetailObject.is_screening;

  const allViewed =
    screenPerSpecimen ||
    (!!caseDetailObject.slides.length &&
      caseDetailObject.slides.every(
        // Slide was previously viewed OR is the one they're looking at right now
        (s) => s.was_previously_viewed || s.id === slideId
      ));

  const handleOnClick = () => {
    if (!waitingForScreening) {
      if (screenPerSpecimen) {
        setShowSpecimens(!showSpecimens);
      } else {
        dialog?.includes("diagnosis") && setDialog && setDialog(null);
        caseDetailObject.id &&
          dispatch(requestScreen({ id: caseDetailObject.id }));
        setScreenerRequested(true);
      }
    }
  };

  /*
    We check for currentUser alone first to make sure that we don't
    accidentally undefined === undefined

    If the current user is the active assignment, display the button
    If the current user is NOT the active assignment, but the case is in screening,
    display the button (it will read Screen Requested)
  */
  return caseDetailObject.status !== Case.status.LIS_PENDING &&
    !isFinalized(caseDetailObject) &&
    currentUser?.id &&
    (!caseDetailObject.is_screening ||
      caseDetailObject.finalize_assignment?.id === currentUser.id) &&
    (caseDetailObject.active_assignment?.id === currentUser.id ||
      caseDetailObject.ordering_doctor?.id === currentUser.id ||
      waitingForScreening) ? (
    <div className="RequestButton__container">
      <button
        className={classNames(
          "Button",
          waitingForScreening ? "Button--white" : "Button--grey"
        )}
        disabled={!allViewed}
        onClick={handleOnClick}
        style={waitingForScreening ? { cursor: "default" } : undefined}
      >
        {waitingForScreening
          ? "Screen Requested"
          : isScreened(caseDetailObject.case_diagnosis)
          ? "Rescreen"
          : "Request Screen"}
        {!waitingForScreening && screenPerSpecimen && (
          <FontAwesomeIcon
            icon="caret-down"
            style={{ marginLeft: "0.3em", fontSize: "1.1em" }}
          />
        )}
      </button>
      {showSpecimens && (
        <SpecimenSelector
          caseDetailObject={caseDetailObject}
          slideId={slideId}
          onClose={() => setShowSpecimens(false)}
          onSelect={(selectedSpecimens: number[]) => {
            dialog?.includes("diagnosis") && setDialog && setDialog(null);
            caseDetailObject.id &&
              dispatch(
                requestScreen({
                  id: caseDetailObject.id,
                  // @ts-ignore
                  requestBody: { selected_specimens: selectedSpecimens },
                })
              );
            setShowSpecimens(false);
            setScreenerRequested(true);
          }}
        />
      )}
    </div>
  ) : null;
};

export default RequestScreenButton;
